import type { User } from "./authAtoms";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3001/api";

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem("excalidraw-auth-token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Check if a username exists and is enabled
 */
export const checkUsername = async (
  username: string,
): Promise<{
  exists: boolean;
  enabled: boolean;
  mfaConfigured: boolean;
  username?: string;
}> => {
  const response = await fetch(`${API_BASE_URL}/auth/check-username`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ username }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to check username");
  }

  return response.json();
};

/**
 * Login with username and TOTP token
 */
export const login = async (
  username: string,
  token: string,
): Promise<{
  success: boolean;
  token: string;
  user: User;
}> => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ username, token }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Login failed");
  }

  return response.json();
};

/**
 * Logout current user
 */
export const logout = async (): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Logout failed");
  }
};

/**
 * Get current user info (verify session)
 */
export const getCurrentUser = async (): Promise<User> => {
  const response = await fetch(`${API_BASE_URL}/auth/me`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to get user info");
  }

  const data = await response.json();
  return data.user;
};

/**
 * Verify current session is valid
 */
export const verifySession = async (): Promise<{
  valid: boolean;
  user: User;
}> => {
  const response = await fetch(`${API_BASE_URL}/auth/verify-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    return { valid: false, user: null as any };
  }

  return response.json();
};

/**
 * Setup MFA for a user (returns QR code URL and secret)
 */
export const setupMFA = async (
  username: string,
  password: string,
): Promise<{
  success: boolean;
  qrCodeUrl: string;
  secret: string;
  message: string;
}> => {
  const response = await fetch(`${API_BASE_URL}/auth/setup-mfa`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to setup MFA");
  }

  return response.json();
};

/**
 * Complete MFA setup with verification code
 */
export const completeMFASetup = async (
  username: string,
  token: string,
): Promise<{
  success: boolean;
  token: string;
  user: User;
}> => {
  const response = await fetch(`${API_BASE_URL}/auth/complete-mfa-setup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ username, token }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to complete MFA setup");
  }

  return response.json();
};

// Admin API calls

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  role: string;
  isAdmin: boolean;
  enabled: boolean;
  createdAt: string;
  lastModified: string;
  mfaConfigured: boolean;
}

/**
 * List all users (admin only)
 */
export const listUsers = async (): Promise<AdminUser[]> => {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to list users");
  }

  const data = await response.json();
  return data.users;
};

/**
 * Create new user (admin only)
 */
export const createUser = async (userData: {
  username: string;
  email: string;
  isAdmin: boolean;
}): Promise<{ success: boolean; user: AdminUser; warning?: string }> => {
  const response = await fetch(`${API_BASE_URL}/admin/users`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to create user");
  }

  return response.json();
};

/**
 * Update user (admin only)
 */
export const updateUser = async (
  userId: string,
  updates: {
    email?: string;
    enabled?: boolean;
    isAdmin?: boolean;
    role?: string;
  },
): Promise<{ success: boolean; user: AdminUser }> => {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update user");
  }

  return response.json();
};

/**
 * Delete user (admin only)
 */
export const deleteUser = async (userId: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to delete user");
  }
};

/**
 * Regenerate MFA for user (admin only)
 */
export const regenerateMFA = async (
  userId: string,
): Promise<{ success: boolean; warning?: string }> => {
  const response = await fetch(
    `${API_BASE_URL}/admin/users/${userId}/regenerate-mfa`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to regenerate MFA");
  }

  return response.json();
};
